/**
 * Reading a message off a caught value, safely.
 *
 * `catch (err: any)` appeared 61 times in this codebase, and the `any` was never
 * the point — it was there so `err.message` would compile. But `throw` accepts
 * any value at all: a string, an object from a rejected fetch, a Supabase
 * PostgrestError (which carries `message`, but often puts the useful part in
 * `details` or `hint`), or `undefined`. `err.message` on a thrown string is
 * `undefined`, which is exactly how an error toast comes to read "undefined".
 *
 * `strict: true` is on, so simply dropping the annotation makes the caught value
 * `unknown` — which is what it genuinely is. This is the narrowing.
 */

import * as Sentry from '@sentry/react';

/** Shape of the errors Supabase returns. Not an Error instance, so `instanceof` misses it. */
interface MessageLike {
    message?: unknown;
    details?: unknown;
    hint?: unknown;
    error_description?: unknown;
}

/**
 * A human-readable message for any caught value.
 *
 * Falls back to `fallback` rather than to "undefined" or "[object Object]" —
 * both of those reach users, and both are worse than a generic sentence.
 */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    if (typeof err === 'string' && err.trim()) return err;

    if (err instanceof Error && err.message) return err.message;

    if (err && typeof err === 'object') {
        const e = err as MessageLike;
        // `error_description` first: Supabase Auth puts the readable text there
        // and leaves `message` as a terse code.
        for (const candidate of [e.error_description, e.message, e.details, e.hint]) {
            if (typeof candidate === 'string' && candidate.trim()) return candidate;
        }
    }
    return fallback;
}

/** The `code` on a caught value, e.g. a Postgres SQLSTATE. Null when there is none. */
export function errorCode(err: unknown): string | null {
    if (err && typeof err === 'object') {
        const code = (err as { code?: unknown }).code;
        if (typeof code === 'string' && code) return code;
    }
    return null;
}

/**
 * Report a failure that the UI is going to swallow.
 *
 * This exists because of how findings 1 and 7 of the 2026-08-17 end-to-end test
 * stayed invisible. Both were `await`ed Supabase calls written as
 *
 *     const { data } = await supabase.from('jobs').select(...)
 *
 * — the `error` never destructured, so nothing threw, nothing logged, and Sentry
 * never heard about it. One of them (`NewJobModal` selecting two columns that do
 * not exist) returned 400 on *every* call for weeks while the panel simply
 * rendered empty. The error reporting looked healthier than the app was.
 *
 * So: when a Supabase call can fail without the user needing to see it, do not
 * drop the binding. Take it and pass it here.
 *
 *     const { data, error } = await supabase.from('jobs').select(...);
 *     if (error) reportError(error, 'new-job.vehicle-history');
 *
 * `area` is a stable dot-delimited tag — it becomes the Sentry `area` tag, which
 * is what makes these greppable in the issue list rather than 100 anonymous
 * "PostgrestError" rows. Use the same string for the same call site over time.
 */
export function reportError(err: unknown, area: string, extra?: Record<string, unknown>): void {
    if (!err) return;

    // PostgREST raises PGRST116 when `.single()` matches no row. That is a
    // routine, expected state across this app, not a fault: `checkOrg` runs it
    // against organization_members for every brand-new user, who by definition
    // has no membership yet. Reporting it would fire on every signup and bury
    // the failures worth looking at.
    //
    // Callers already tolerate absence — they were all written against
    // possibly-undefined data — so nothing is lost by staying quiet here. A
    // caller that genuinely needs to act on "row missing" should test the data
    // for null, which is clearer at the call site than a Sentry issue anyway.
    if (errorCode(err) === 'PGRST116') return;

    // Console first and unconditionally: Sentry is off when SENTRY_DSN is unset
    // (local dev, and any deploy where it was never configured), and a swallowed
    // failure with no console trace is the exact problem this function exists for.
    console.error(`[${area}]`, err, extra ?? '');

    try {
        Sentry.captureException(err, {
            tags: { area },
            extra: { ...extra, code: errorCode(err), message: errorMessage(err, '') },
        });
    } catch {
        // Reporting must never be the thing that breaks a screen.
    }
}
